// src/components/CreateAccount.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import memberstackDOM from "@memberstack/dom";
import { getMemberstackKey } from "../utils";
import mixpanel from "mixpanel-browser";
import HelpFeedback from "./HelpFeedback";
import { getStoredLandingPage, getViewedBlogSlugs, getOrCreateAnonId, captureFbcId, getFbcId, getFbpId } from "../utils/sourceTracking";

// Add after imports, before component definition
// Generate unique event ID for deduplication
function generateEventId() {
  return Date.now().toString() + '-' + Math.random().toString(36).substr(2, 9);
}

// Initialize FBC capture
captureFbcId();

// Initialize Memberstack using the public key from our utils.
const currentKey = getMemberstackKey();
const memberstack = memberstackDOM.init({
  publicKey: currentKey,
});

const CreateAccount = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isAcceptsTermsChecked, setIsAcceptsTermsChecked] = useState(false);
  const [optInMarketing, setOptInMarketing] = useState(false);

  // Capture NCC parameters from URL on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const nccParam = urlParams.get('ncc');
    const campaignParam = urlParams.get('campaign');

    // Store ncc flag in localStorage if present
    if (nccParam === 'true') {
      localStorage.setItem('ncc', 'true');
      if (campaignParam) {
        localStorage.setItem('ncc_campaign', campaignParam);
      }
    }
  }, []);

  // A landing page (?lp=slug) may set a different checkout price - carried
  // through as a URL param (not just sessionStorage) so it survives ad
  // blockers/incognito storage restrictions. Re-stored here as a fallback
  // too, and forwarded on to /checkout below so Checkout.js can resolve
  // the actual price fresh from the backend.
  const [landingPageSlug] = useState(() => {
    const fromUrl = new URLSearchParams(window.location.search).get('lp');
    const slug = fromUrl || sessionStorage.getItem('landingPageSlug') || '';
    if (slug) {
      sessionStorage.setItem('landingPageSlug', slug);
    }
    return slug;
  });

  // track form view
  useEffect(() => {
    if (mixpanel && mixpanel.__loaded) {
      mixpanel.track("CreateAccount: Form Viewed");
    }
  }, []);

  const handleSignup = async (event) => {
    event.preventDefault();
    setLoading(true);
    setErrorMessage("");
    if (mixpanel && mixpanel.__loaded) {
      mixpanel.track("CreateAccount: Attempt Signup");
      mixpanel.track("CreateAccount: Marketing Opt-In", { optedIn: optInMarketing });
    }

    if (!isAcceptsTermsChecked) {
      setErrorMessage(
        "You must confirm you are over 18 years old and accept the terms and conditions to continue."
      );
      if (mixpanel && mixpanel.__loaded) {
        mixpanel.track("CreateAccount: Terms Not Accepted");
      }
      setLoading(false);
      return;
    }

    const email = document.getElementById("email").value;
    const password = document.getElementById("password").value;

    const emailRegex = /\S+@\S+\.\S+/;
    if (!emailRegex.test(email)) {
      const msg =
        "This email address is invalid or already in use. Please try another one or log in.";
      setErrorMessage(msg);
      if (mixpanel && mixpanel.__loaded) {
        mixpanel.track("CreateAccount: Invalid Email", { email });
      }
      setLoading(false);
      return;
    }

    const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d).{8,}$/;
    if (!passwordRegex.test(password)) {
      const msg =
        "Your password must be at least 8 characters and include at least one letter and one number.";
      setErrorMessage(msg);
      if (mixpanel && mixpanel.__loaded) {
        mixpanel.track("CreateAccount: Invalid Password");
      }
      setLoading(false);
      return;
    }

    try {
      // sign up
      const response = await memberstack.signupMemberEmailPassword({ email, password });

      if (response.error) {
        const err = response.error.toLowerCase();
        if (err.includes("password")) {
          setErrorMessage(
            "Your password must be at least 8 characters long and include both letters and numbers."
          );
        } else if (err.includes("email")) {
          setErrorMessage(
            "This email address is already in use. Please use a different email address or log in."
          );
        } else {
          setErrorMessage(response.error);
        }
        if (mixpanel && mixpanel.__loaded) {
          mixpanel.track("CreateAccount: Memberstack Signup Failed", { error: response.error });
        }
        setLoading(false);
        return;
      }
      if (mixpanel && mixpanel.__loaded) {
        mixpanel.track("CreateAccount: Memberstack Signup Success");
      }

      // extract token
      const token = response?.data?.tokens?.accessToken;
      if (!token) {
        const msg = "Failed to create account. No access token received from Memberstack.";
        setErrorMessage(msg);
        if (mixpanel && mixpanel.__loaded) {
          mixpanel.track("CreateAccount: Signup Error", { error: msg });
        }
        setLoading(false);
        return;
      }
      localStorage.setItem("memberstackToken", token);

      // Register the user in the database
      try {
        const eventId = generateEventId();
        const userSource = localStorage.getItem("user_source");
        const landingPage = getStoredLandingPage();
        const landingPageVariant = localStorage.getItem("landing_page_variant");
        const fbcId = getFbcId();
        const fbpId = getFbpId();
        // Blog posts read anonymously, before we had a user_id to write a
        // durable blog_post_views row against - backfilled now that
        // first_signup actually creates that user_id (see
        // recordBlogPostView in sourceTracking.js / BlogPost.js).
        const viewedBlogSlugs = getViewedBlogSlugs();

        const firstSignupHeaders = {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        };
        if (fbcId) firstSignupHeaders['X-Fbc'] = fbcId;
        if (fbpId) firstSignupHeaders['X-Fbp'] = fbpId;

        const firstSignupResponse = await fetch('/api/signup/firstSignup', {
          method: 'POST',
          headers: firstSignupHeaders,
          credentials: 'include',
          body: JSON.stringify({
            email,
            firstName: "",
            lastName: "",
            marketingOptIn: optInMarketing,
            user_source: userSource || null,
            landing_page: landingPage || null,
            landing_page_variant: landingPageVariant || null,
            // Lets the backend fall back to the most recent page_views row
            // for this browser when landing_page above is missing (cleared
            // storage, a different tab) - see models.PageView's docstring.
            anon_id: getOrCreateAnonId(),
            viewed_blog_slugs: viewedBlogSlugs,
            event_id: eventId,
          })
        });

        // Merge the anonymous pre-signup Mixpanel session into an identified
        // profile keyed by our database user id, so server-sent events (e.g.
        // a confirmed trial-start from a webhook) can be attributed back to
        // this visitor's original landing page/source.
        if (firstSignupResponse.ok && mixpanel && mixpanel.__loaded) {
          const { user_id: newUserId } = await firstSignupResponse.json();
          if (newUserId) {
            mixpanel.identify(String(newUserId));
          }
        }

        try {
          window.fbq?.('track', 'CompleteRegistration', {}, { eventID: eventId });
        } catch (_) {}
      } catch (err) {
        // Non-critical — don't block the user
      }

      if (mixpanel && mixpanel.__loaded) {
        mixpanel.track("CreateAccount: Successful Signup");
      }

      navigate(landingPageSlug ? `/checkout?lp=${encodeURIComponent(landingPageSlug)}` : "/checkout");

    } catch (error) {
      const errMsg = error.message.toLowerCase();
      if (errMsg.includes("password")) {
        setErrorMessage(
          "Your password does not meet the requirements (8+ chars, letters & numbers)."
        );
      } else if (errMsg.includes("email")) {
        setErrorMessage(
          "This email address is invalid or already in use. Please try another one or log in."
        );
      } else {
        setErrorMessage("An unexpected error occurred. Please check your details and try again.");
      }
      if (mixpanel && mixpanel.__loaded) {
        mixpanel.track("CreateAccount: Signup Error", { error: error.message });
      }
      setLoading(false);
    }
  };

  return (
    <div className="page-wrapper">
      <main className="main-wrapper">
        <section className="section_hero">
          <div className="padding-global padding-section-large">
            <div className="container-large" style={{ position: "relative" }}>
              <div className="hero_card is-vertical" style={{ position: "relative" }}>
                <HelpFeedback />
                <div className="custom-modal">
                  <div className="modal-content">

                    <>
                      <h4 className="hero_heading-text text-align-center margin-bottom margin-small">
                        Create Your Account
                      </h4>
                      <p style={{ textAlign: "center", fontSize: "0.95rem", color: "#555", marginBottom: "20px" }}>
                        Unlimited events &amp; 20% off food and drinks at the venue.
                      </p>
                      <form onSubmit={handleSignup}>
                        <label htmlFor="email">Email Address</label>
                        <input
                          className="hero_form-textfield w-input"
                          type="email"
                          id="email"
                          name="email"
                          required
                        />

                        <label htmlFor="password">Password</label>
                        <input
                          className="hero_form-textfield w-input"
                          type="password"
                          id="password"
                          name="password"
                          required
                        />

                        {/* Checkbox for confirming age & T&Cs */}
                        <div
                          style={{ display: "flex", alignItems: "center", margin: "0rem 0" }}
                        >
                          <input
                            type="checkbox"
                            id="acceptsTermsCheck"
                            checked={isAcceptsTermsChecked}
                            onChange={(e) => setIsAcceptsTermsChecked(e.target.checked)}
                            style={{ marginRight: "0.5rem" }}
                          />
                          <label
                            htmlFor="acceptsTermsCheck"
                            style={{ fontSize: "0.7rem", fontWeight: "normal" }}
                          >
                            <p style={{ marginTop: "1px", fontSize: "0.7rem" }}>
                              By signing up you confirm that you are over 18 years old and agree to
                              our{" "}
                              <a
                                href="/terms"
                                style={{
                                  fontWeight: "bold",
                                  color: "inherit",
                                  textDecoration: "underline",
                                }}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                Terms of Service
                              </a>{" "}
                              and{" "}
                              <a
                                href="/privacy"
                                style={{
                                  fontWeight: "bold",
                                  color: "inherit",
                                  textDecoration: "underline",
                                }}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                Privacy Policy
                              </a>
                              .
                            </p>
                          </label>
                        </div>

                        {/* NEW checkbox for marketing opt-in */}
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            margin: "0rem 0 0.5rem",
                          }}
                        >
                          <input
                            type="checkbox"
                            id="marketingOptIn"
                            checked={optInMarketing}
                            onChange={(e) => setOptInMarketing(e.target.checked)}
                            style={{ marginRight: "0.5rem" }}
                          />
                          <label
                            htmlFor="marketingOptIn"
                            style={{ marginTop: "1px", fontSize: "0.7rem", fontWeight: "normal" }}
                          >
                            I agree to receive occasional updates, event news and exclusive
                            offers and understand that I can unsubscribe at any time.
                          </label>
                        </div>

                        {errorMessage && (
                          <p className="error-message" style={{ color: "red" }}>
                            {errorMessage}
                          </p>
                        )}

                        <button
                          type="submit"
                          className="button-2 is-black w-button"
                          disabled={loading}
                        >
                          {loading ? "Creating Account..." : "Create Account"}
                        </button>
                      </form>
                    </>

                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default CreateAccount;
